// ObservationCardExtended.jsx
import React, { useEffect, useState } from "react";
import { useStore } from "react-redux";
import styles from "../modals/ResourceViewerModal.module.css";
import { resolveName, hydrateFhirGraph } from "../utils/messageUtils";
import { formatLocalDate } from "../utils/dateUtils";
import { fetchPatientByReference } from "../services/patientService";
import { fetchProviderByReference } from "../services/providerService";
import { fetchOrganizationByReference } from "../services/organizationService";
import { fetchEncounterByReference } from "../services/encounterService";
import CardHeader from "./CardHeader";
import { Field, renderReference, SectionHeader, renderCodeable } from "../utils/renderUtils";


const renderQuantity = (q) =>
  q ? (
    <>
      {q.value} {q.unit && <span>({q.unit})</span>}
      <div className="text-muted small">{q.system}</div>
    </>
  ) : (
    <span className="text-muted">N/A</span>
  );

const renderValueX = (obs) => {
  if (!obs) return <span className="text-muted">No value reported</span>;

  const value = obs.valueQuantity || obs.valueCodeableConcept || obs.valueString || obs.valueBoolean !== undefined ||
    obs.valueInteger !== undefined || obs.valueRange || obs.valueRatio || obs.valueDateTime ||
    obs.valueTime || obs.valuePeriod || obs.valueSampledData;

  if (!value && obs.dataAbsentReason) {
    return <span className="text-muted">No value reported ({obs.dataAbsentReason.text || obs.dataAbsentReason.code})</span>;
  }

  if (obs.valueQuantity) return renderQuantity(obs.valueQuantity);
  if (obs.valueCodeableConcept) return renderCodeable(obs.valueCodeableConcept);
  if (obs.valueString) return <span>{obs.valueString}</span>;
  if (obs.valueBoolean !== undefined) return <span>{obs.valueBoolean ? "True" : "False"}</span>;
  if (obs.valueInteger !== undefined) return <span>{obs.valueInteger}</span>;
  if (obs.valueRange)
    return (
      <>
        Low: {renderQuantity(obs.valueRange.low)} <br />
        High: {renderQuantity(obs.valueRange.high)}
      </>
    );
  if (obs.valueRatio)
    return (
      <>
        Numerator: {renderQuantity(obs.valueRatio.numerator)} <br />
        Denominator: {renderQuantity(obs.valueRatio.denominator)}
      </>
    );
  if (obs.valueDateTime) return <span>{obs.valueDateTime}</span>;
  if (obs.valueTime) return <span>{obs.valueTime}</span>;
  if (obs.valuePeriod)
    return (
      <>
        From: {obs.valuePeriod.start || "?"} <br />
        To: {obs.valuePeriod.end || "?"}
      </>
    );
  if (obs.valueSampledData)
    return <span className="text-muted small">SampledData (graph not implemented)</span>;

  return <span className="text-muted">No value reported</span>;
};


/* -------------------- MAIN COMPONENT -------------------- */
const ObservationCardExtended = ({ resource, handleShowNested, fhirClient }) => {
  //const [loading, setLoading] = useState({ patient: false, provider: false, org: false });

  const store = useStore();

  //SUBJECT
  const [subject, setSubject] = useState(null);
  const [subjectName, setSubjectName] = useState(null);
  //const subjectName = resolveName(subject, store);

  //Encounter
  const [encounterData, setEncounterData] = useState(null);
  const [encounterName, setEncounterName] = useState(null);
  //console.log(encounterName);

  //PERFORMERS
  const [performers, setPerformers] = useState([]);

  //PRIMARY PRACTITIONER (PERFORMER)
  const [provider, setProvider] = useState(null);

  //PRIMARY ORGANIZATION (PERFORMER)
  const [organization, setOrganization] = useState(null);

  useEffect(() => {
    if (!resource || !fhirClient) return;

    console.log("hydrating fhir graph...");
    hydrateFhirGraph(resource, fhirClient, store);

    const doFetchResources = async () => {
      // ---------- SUBJECT ----------
      if (resource.subject?.reference) {
        const patient = await fetchPatientByReference(
          resource.subject.reference,
          fhirClient,
          store
        );
        setSubject(patient);
        setSubjectName(resolveName(patient));
      }

      // ---------- ENCOUNTER ----------
      if (resource.encounter?.reference) {
        const encounter = await fetchEncounterByReference(
          resource.encounter.reference,
          fhirClient,
          store
        );
        setEncounterData(encounter);
        setEncounterName(resolveName(encounter));
      }

      // ---------- PERFORMERS ----------
      //We take the first provider and org we encounter for display with photos. The whole list is provided in the performers section.
      const resolvedPerformers = [];

      let primaryProvider = null;
      let primaryOrganization = null;

      for (const performer of resource.performer ?? []) {
        const ref = performer?.reference;
        if (!ref) continue;

        const [type] = ref.split("/");

        let resolved = null;

        switch (type) {
          case "Practitioner":
            resolved = await fetchProviderByReference(ref, fhirClient, store);

            // first practitioner only
            if (!primaryProvider) {
              primaryProvider = resolved;
              setProvider(resolved);
            }
            break;

          case "Organization":
            resolved = await fetchOrganizationByReference(ref, fhirClient, store);

            // first organization only
            if (!primaryOrganization) {
              primaryOrganization = resolved;
              setOrganization(resolved);
            }
            break;

          case "Patient":
            resolved = await fetchPatientByReference(ref, fhirClient, store);
            break;

          default:
            // leave unresolved but still keep the reference
            resolved = ref;
        }

        resolvedPerformers.push(resolved || ref);
      }

      setPerformers(resolvedPerformers);
    };

    doFetchResources();
  }, [resource, fhirClient, store]);


  const identifierList = resource.identifier || [];
  const categories = resource.category || [];
  const components = resource.component || [];
  const interpretation = resource.interpretation || [];
  const referenceRange = resource.referenceRange || [];

  return (
    <div className={`p-4 border rounded shadow-sm bg-white`}>
      {/* ===================== SUBJECT + PERFORMER (Patient, Provider, Org) ===================== */}
        <CardHeader resource={resource} handleShowNested={handleShowNested} fhirClient={fhirClient} />


      {/* ===================== PERFORMERS ===================== */}
      {resource.performer?.length > 0 && (
        <>
          <SectionHeader>Performers</SectionHeader>
          <div className={styles.indented}>
            <div className={styles.sectionContent}>
              {resource.performer.map((p, idx) => {
                const ref = p.reference || (typeof p === "string" ? p : null);
                const displayName = resolveName(ref || p.display || p, store) || p.display || ref || "Unknown performer";
                return ref ? (
                  <div key={idx} className="mb-1">
                    <button
                      className="btn btn-link p-0"
                      onClick={() => handleShowNested?.(ref, displayName)}
                    >
                      {displayName}
                    </button>
                  </div>
                ) : (
                  <div key={idx} className="mb-1 text-muted">{displayName}</div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ===================== IDENTIFICATION ===================== */}
      <SectionHeader>Identification</SectionHeader>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          <Field label="Observation ID">{resource.id}</Field>
          <Field label="Status">{resource.status}</Field>
          <Field label="Code">{renderCodeable(resource.code)}</Field>
          {identifierList.length > 0 && (
            <Field label="Identifiers">
              {identifierList.map((id, idx) => (
                <div key={idx} className="ps-3">{id.system} — {id.value}</div>
              ))}
            </Field>
          )}
          {categories.length > 0 && (
            <Field label="Categories">
              {categories.map((cat, idx) => (
                <div key={idx} className="mb-2">{renderCodeable(cat)}</div>
              ))}
            </Field>
          )}
        </div></div>

      {/* ===================== TIMING ===================== */}
      <SectionHeader>Timing</SectionHeader>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          <Field label="Effective">
            {resource.effectiveDateTime
              ? formatLocalDate(resource.effectiveDateTime)
              : resource.effectiveInstant
                ? formatLocalDate(resource.effectiveInstant)
                : resource.effectivePeriod
                  ? `${formatLocalDate(resource.effectivePeriod.start) || "?"} → ${formatLocalDate(resource.effectivePeriod.end) || "?"}`
                  : resource.effectiveTiming
                    ? "(Timing)"
                    : "N/A"}
          </Field>

          <Field label="Issued">
            {resource.issued ? formatLocalDate(resource.issued) : "N/A"}
          </Field>


          <Field label="Encounter">
            {encounterName ? (
              <button
                className="btn btn-link p-0"
                onClick={() => handleShowNested?.(encounterData, encounterName)}
              >
                {encounterName}
              </button>
            ) : (
              <span className="text-muted">N/A</span>
            )}
          </Field>
        </div></div>

      {/* ===================== VALUE ===================== */}
      <SectionHeader>Observation Value</SectionHeader>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          {resource.dataAbsentReason && !resource.valueQuantity && (
            <Field label="Data Absent Reason">{renderCodeable(resource.dataAbsentReason)}</Field>
          )}
          <Field label="Value">{renderValueX(resource)}</Field>
        </div></div>

      {/* ===================== INTERPRETATION ===================== */}
      {interpretation.length > 0 && (
        <>
          <SectionHeader>Interpretation</SectionHeader>
          <div className={styles.indented}>
            <div className={styles.sectionContent}>
              {interpretation.map((i, idx) => (
                <div key={idx} className="mb-2">{renderCodeable(i)}</div>
              ))}
            </div></div>
        </>
      )}

      {/* ===================== PROCEDURE CONTEXT ===================== */}
      <SectionHeader>Procedure Context</SectionHeader>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          <Field label="Body Site">{renderCodeable(resource.bodySite)}</Field>
          <Field label="Method">{renderCodeable(resource.method)}</Field>
          <Field label="Specimen">{renderReference(resource.specimen?.reference, "Specimen", handleShowNested)}</Field>
          <Field label="Device">{renderReference(resource.device?.reference, "Device", handleShowNested)}</Field>
        </div></div>

      {/* ===================== REFERENCE RANGE ===================== */}
      {referenceRange.length > 0 && (
        <>
          <SectionHeader>Reference Ranges</SectionHeader>
          <div className={styles.indented}>
            {referenceRange.map((rr, idx) => (
              <div key={idx} className="border rounded p-2 mb-2 bg-light">
                <Field label="Low">{renderQuantity(rr.low)}</Field>
                <Field label="High">{renderQuantity(rr.high)}</Field>
                <Field label="Type">{renderCodeable(rr.type)}</Field>
                {rr.appliesTo?.length > 0 && (
                  <Field label="Applies To">
                    {rr.appliesTo.map((ap, i2) => (
                      <div key={i2} className="ps-3">{renderCodeable(ap)}</div>
                    ))}
                  </Field>
                )}
                {rr.age && (
                  <Field label="Age Range">{rr.age.low?.value} → {rr.age.high?.value}</Field>
                )}
                <Field label="Text">{rr.text}</Field>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ===================== COMPONENTS ===================== */}
      {components.length > 0 && (
        <>
          <SectionHeader>Components</SectionHeader>
          <div className={styles.indented}>
            {components.map((comp, idx) => (
              <div key={idx} className="border rounded p-2 mb-3 bg-light">
                <Field label="Code">{renderCodeable(comp.code)}</Field>
                <Field label="Value">{renderValueX(comp)}</Field>
                {comp.dataAbsentReason && (
                  <Field label="Data Absent Reason">{renderCodeable(comp.dataAbsentReason)}</Field>
                )}
                {comp.interpretation?.length > 0 && (
                  <Field label="Interpretation">
                    {comp.interpretation.map((i2, j) => (
                      <div key={j} className="ps-3">{renderCodeable(i2)}</div>
                    ))}
                  </Field>
                )}
                {comp.referenceRange?.length > 0 && (
                  <Field label="Reference Ranges">
                    {comp.referenceRange.map((rr, k) => (
                      <div key={k} className="ps-3 mb-2">
                        Low: {renderQuantity(rr.low)} <br />
                        High: {renderQuantity(rr.high)}
                      </div>
                    ))}
                  </Field>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* ===================== NOTES ===================== */}
      {resource.note?.length > 0 && (
        <>
          <SectionHeader>Notes</SectionHeader>
          <div className={styles.indented}>
            <div className={styles.sectionContent}>
              {resource.note.map((n, idx) => (
                <div key={idx} className="mb-2">
                  {n.text} <span className="text-muted small">({n.authorString || n.authorReference?.reference})</span>
                </div>
              ))}
            </div></div>
        </>
      )}

      {/* ===================== RELATIONSHIPS ===================== */}
      <SectionHeader>Relationships</SectionHeader>
      <div className={styles.indented}>

        <Field label="Based On">
          {resource.basedOn?.map((r, idx) =>
            r.reference ? (
              <button
                key={idx}
                className="btn btn-link p-0"
                onClick={() => handleShowNested?.(r.reference, resolveName(r.reference, store))}
              >
                {resolveName(r.reference, store)}
              </button>
            ) : (
              <span key={idx} className="text-muted">N/A</span>
            )
          )}
        </Field>


        <Field label="Part Of">
          {resource.partOf?.map((r, idx) =>
            r.reference ? (
              <button
                key={idx}
                className="btn btn-link p-0"
                onClick={() => handleShowNested?.(r.reference, resolveName(r.reference, store))}
              >
                {resolveName(r.reference, store)}
              </button>
            ) : (
              <span key={idx} className="text-muted">N/A</span>
            )
          )}
        </Field>

        <Field label="Has Member">
          {resource.hasMember?.map((r, idx) =>
            r.reference ? (
              <button
                key={idx}
                className="btn btn-link p-0"
                onClick={() => handleShowNested?.(r.reference, resolveName(r.reference, store))}
              >
                {resolveName(r.reference, store)}
              </button>
            ) : (
              <span key={idx} className="text-muted">N/A</span>
            )
          )}
        </Field>

        <div className={styles.sectionContent}>
          <Field label="Derived From">
            {resource.derivedFrom?.map((r, idx) =>
              r.reference ? (
                <button
                  key={idx}
                  className="btn btn-link p-0"
                  onClick={() => handleShowNested?.(r.reference, resolveName(r.reference, store))}
                >
                  {resolveName(r.reference, store)}
                </button>
              ) : (
                <span key={idx} className="text-muted">N/A</span>
              )
            )}
          </Field>


        </div></div>


      {/* ===================== METADATA ===================== */}
      <SectionHeader>Metadata</SectionHeader>
      <div className={styles.indented}>
        <Field label="Language">{resource.language}</Field>
        <Field label="Last Updated">
          {resource.meta?.lastUpdated
            ? formatLocalDate(resource.meta.lastUpdated)
            : "N/A"}
        </Field>

        <Field label="Version">{resource.meta?.versionId}</Field>
      </div>
    </div>
  );

};

export default ObservationCardExtended;
